<script setup lang="ts">
import { defineProps, defineEmits, withDefaults, computed, ref, watch } from "vue";
import SidebarNavigation from "./sidebarNavigation.vue";

const props = withDefaults(
    defineProps<{
      filter: any;
      activeValue: any;
      singleSelect?: boolean;
    }>(),
    {
      filter: () => ({values: {}}),
      activeValue: () => [],
      singleSelect: false,
    }
);

const emit = defineEmits<{
  (e: "update", value: any): void;
}>();

const itemKeys = computed(() => Object.keys(props.filter.values));
const itemRefs = ref<HTMLElement[]>([]);
const scrollContainer = ref<HTMLElement | null>(null);
const manualIndex = ref(0);

const currentIndex = computed({
  get: () => manualIndex.value,
  set: (val) => {
    manualIndex.value = val;
    scrollToIndex(val);
  }
});

const scrollToIndex = (index: number) => {
  const el = itemRefs.value[index];
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
};

const onScroll = () => {
  if (!scrollContainer.value) return;
  const container = scrollContainer.value;
  const containerCenter = container.scrollLeft + container.clientWidth / 2;

  let closestIndex = 0;
  let minDistance = Infinity;

  itemRefs.value.forEach((el, index) => {
    if (!el) return;
    const elCenter = el.offsetLeft + el.clientWidth / 2;
    const distance = Math.abs(elCenter - containerCenter);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = index;
    }
  });

  manualIndex.value = closestIndex;
};

watch(() => props.activeValue, (newVal) => {
  const val = Array.isArray(newVal) ? newVal[0] : newVal;
  const idx = itemKeys.value.indexOf(val?.toString() || "");
  if (idx >= 0) {
    manualIndex.value = idx;
  }
}, { immediate: true });

const toggleValue = (val: string) => {
  if (props.singleSelect) {
    emit("update", val);
  } else {
    let newValue = Array.isArray(props.activeValue) ? [...props.activeValue] : [];
    const index = newValue.indexOf(val);
    if (index > -1) {
      newValue.splice(index, 1);
    } else {
      newValue.push(val);
    }
    emit("update", newValue);
  }
};

const isActive = (val: string) => {
  return (Array.isArray(props.activeValue) && props.activeValue.includes(val)) || props.activeValue === val || (val == "" && !props.activeValue.includes(val));
};
</script>

<template>
  <div class="flex flex-col">
    <div
        ref="scrollContainer"
        class="flex gap-8 pb-4 px-2 overflow-x-auto no-scrollbar scroll-smooth"
        @scroll="onScroll"
    >
      <div
          v-for="(label, val, index) in filter.values"
          :key="val"
          :ref="el => { if (el) itemRefs[index] = el as any }"
          class="flex flex-col items-center cursor-pointer min-w-fit justify-end"
          @click="toggleValue(val.toString())"
      >
        <div
            class="px-4 py-2 flex items-center justify-center mb-2 transition-all duration-300 overflow-hidden"
            :class="isActive(val.toString()) ? 'border-b border-brand-main' : ''"
        >
          <div class="text-xs font-medium text-gray-700">
            {{ label }}
          </div>
        </div>
      </div>
    </div>
    <sidebar-navigation
        :total="itemKeys.length"
        v-model="currentIndex"
        :container="scrollContainer"
    />
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>